import { CommonModule } from '@angular/common';
import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    inject
} from '@angular/core';
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    ValidationErrors
} from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import {
    IAddressRegionDto,
    IBuildTypeDto,
    LOT_CONTRACT_TYPE_ENUM,
    LOT_TYPE_ENUM,
    LotTypeEnumId
} from '@classictechsolutions/hubapi-transpiled-enums';
import { MatStepperModule } from '@angular/material/stepper';
import { ILotAdd } from '../lot-migrator.models';
import { ILotMappedItem } from '@app/logic/lots';
import { LotMigratorBlockFormComponent } from './block-form/lot-migrator-block-form.component';
import { LotMigratorUnitFormComponent } from './unit-form/lot-migrator-unit-form.component';
import { LotMigratorStandaloneFormComponent } from './standalone-form/lot-migrator-standalone-form.component';
import { LOT_MIGRATOR_FORM_ERRORS } from './lot-migrator-form-errors';
import {
    buildLotMigratorAddressControls,
    buildLotMigratorAreaControls,
    buildLotMigratorBuildTypeLinkControl,
    buildLotMigratorLotBuildControls,
    buildLotMigratorPricingControls,
    buildLotMigratorRoomsAndParkingControls
} from './lot-migrator-shared-form-controls';

@Component({
    selector: 'cb-lot-migrator-lot-forms',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatStepperModule,
        MatCheckboxModule,
        MatFormFieldModule,
        MatSelectModule,
        LotMigratorBlockFormComponent,
        LotMigratorUnitFormComponent,
        LotMigratorStandaloneFormComponent
    ],
    templateUrl: './lot-migrator-lot-forms.component.html',
    styleUrls: ['./lot-migrator-lot-forms.component.scss']
})
export class LotMigratorLotFormsComponent implements OnChanges {
    @Input() public migrationLotType: LotTypeEnumId;
    @Input() public lotAddMap: ILotAdd[] = [];
    @Input() public lotMappedItems: ILotMappedItem[] = [];
    @Input() public unavailableLotNumbers = new Set<number>();
    @Input() public addressRegions: IAddressRegionDto[] = [];
    @Input() public buildTypes: IBuildTypeDto[] = [];
    @Input() public contractTypes = LOT_CONTRACT_TYPE_ENUM.toSelectList();
    @Input() public lotTypes = LOT_TYPE_ENUM.toSelectList();
    @Output() public formsChange = new EventEmitter<FormArray>();
    @Output() public sourceDestinationMappingChange = new EventEmitter<Record<number, number>>();

    private readonly _formBuilder = inject(FormBuilder);
    public lotsFormArray = this._formBuilder.array<FormGroup>([]);
    private selectedSourceLotIdByFormIndex: Partial<Record<number, number>> = {};
    private isSourceLotMappedByFormIndex: Partial<Record<number, boolean>> = {};

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.lotAddMap || changes.migrationLotType) {
            this.resetForms();
            this.lotsFormArray.setValidators([this.uniqueLotNumbersValidator]);
            this.lotsFormArray.updateValueAndValidity();
            this.formsChange.emit(this.lotsFormArray);
        }
    }

    public get lotForms(): FormGroup[] {
        return this.lotsFormArray.controls;
    }

    public handleExistingLotSelectionChange(sourceLotId: number | null, index: number): void {
        if (sourceLotId === null) {
            delete this.selectedSourceLotIdByFormIndex[index];
            this.isSourceLotMappedByFormIndex[index] = false;
            this.emitSourceDestinationMappingChange();
            return;
        }

        this.selectedSourceLotIdByFormIndex[index] = sourceLotId;
        const form = this.lotsFormArray.at(index) as FormGroup;
        const selectedSourceLot = this.lotMappedItems.find(lot => lot.id === sourceLotId);
        form.patchValue(this.lotDtoToFormValue(selectedSourceLot), { emitEvent: true });
        form.markAllAsTouched();
        form.markAsDirty();
        form.updateValueAndValidity({ emitEvent: true });
        this.emitSourceDestinationMappingChange();
    }

    public handleSourceLotMappedChange(index: number, isMapped: boolean): void {
        const sourceLotId = this.selectedSourceLotIdByFormIndex[index];
        const hasSourceLotSelection = sourceLotId != null;

        if (hasSourceLotSelection && isMapped) {
            this.clearSourceLotSelectionFromOtherForms(sourceLotId, index);
        }

        this.isSourceLotMappedByFormIndex[index] = hasSourceLotSelection && isMapped;
        this.emitSourceDestinationMappingChange();
    }

    public getSelectedSourceLotId(index: number): number | null {
        return this.selectedSourceLotIdByFormIndex[index] ?? null;
    }

    public isSourceLotSelected(index: number): boolean {
        return this.selectedSourceLotIdByFormIndex[index] !== undefined;
    }

    public isSourceLotMapped(index: number): boolean {
        return this.isSourceLotMappedByFormIndex[index] === true;
    }

    public isSourceLotMappedByAnotherForm(sourceLotId: number, currentIndex: number): boolean {
        return Object.keys(this.selectedSourceLotIdByFormIndex).some(key => {
            const index = Number(key);
            return (
                index !== currentIndex &&
                this.selectedSourceLotIdByFormIndex[index] === sourceLotId &&
                this.isSourceLotMappedByFormIndex[index] === true
            );
        });
    }

    public isBlockForm(lotForm: FormGroup<any>): boolean {
        return (
            lotForm.get('LotName')?.value.includes('Block') &&
            !lotForm.get('LotName')?.value.includes('Unit')
        );
    }

    public isUnitForm(lotForm: FormGroup<any>): boolean {
        return (
            lotForm.get('LotName')?.value.includes('Unit')
        );
    }

    public getBuildTypesForLotForm(lotForm: FormGroup): IBuildTypeDto[] {
        const selectedLotTypeLabel = lotForm.get('LotType')?.value;
        if (!selectedLotTypeLabel) {
            return [];
        }

        const selectedLotType = this.lotTypes.find(x => x.label === selectedLotTypeLabel);
        if (!selectedLotType) {
            return [];
        }

        return this.buildTypes.filter(x => x.lotType.includes(selectedLotType.id));
    }

    private resetForms(): void {
        this.lotsFormArray.clear();
        this.selectedSourceLotIdByFormIndex = {};
        this.isSourceLotMappedByFormIndex = {};
        this.lotAddMap.forEach(lot => {
            if (this.migrationLotType === LotTypeEnumId.Standalone) {
                this.lotsFormArray.push(this.createStandaloneOrUnitForm(lot.name));
            }

            if (this.migrationLotType === LotTypeEnumId.TerracedBlock) {
                this.lotsFormArray.push(this.createBlockForm(lot.name));
                lot.units.forEach(unit => {
                    this.lotsFormArray.push(this.createStandaloneOrUnitForm(unit.name));
                });
            }
        });

        this.lotsFormArray.controls.forEach(control => {
            const lotNumberControl = control.get('LotNumber');
            lotNumberControl?.valueChanges.subscribe(() => {
                this.lotsFormArray.updateValueAndValidity();
                this.emitSourceDestinationMappingChange();
            });
        });

        this.emitSourceDestinationMappingChange();
    }

    private createBlockForm(name: string): FormGroup {
        const lotType = this.getLotType(name);
        const formGroup = this._formBuilder.group({
            LotName: [name],
            ...buildLotMigratorAddressControls(),
            ...buildLotMigratorLotBuildControls(lotType)
        });

        if (!name.includes('Unit')) {
            const blockAndNumber = name.split('Unit')[0].trim();
            formGroup.get('LotNumber').valueChanges.subscribe(x => {
                this.lotsFormArray.controls.forEach(item => {
                    const formName = item.get('LotName').value;
                    if (formName.includes(blockAndNumber + ' Unit')) {
                        item.get('BuildTypeLink').setValue(x);
                    }
                });
            });
        }

        return formGroup;
    }

    private createStandaloneOrUnitForm(name: string): FormGroup {
        const isUnit = this.isUnitName(name);
        const lotType = this.getLotType(name);
        return this._formBuilder.group({
            LotName: [name],
            ...buildLotMigratorAddressControls(),
            ...buildLotMigratorLotBuildControls(lotType),
            ...buildLotMigratorAreaControls(),
            ...buildLotMigratorPricingControls(),
            JobNumber: [''],
            ...buildLotMigratorRoomsAndParkingControls(),
            ...buildLotMigratorBuildTypeLinkControl(isUnit)
        });
    }

    private emitSourceDestinationMappingChange(): void {
        const map: Record<number, number> = {};

        this.lotForms.forEach((lotForm, index) => {
            const sourceLotId = this.selectedSourceLotIdByFormIndex[index];
            const destinationLotNumber = lotForm.get('LotNumber')?.value;
            const isMapped = this.isSourceLotMappedByFormIndex[index] === true;

            if (!isMapped || sourceLotId == null) {
                return;
            }

            map[sourceLotId] = destinationLotNumber;
        });

        this.sourceDestinationMappingChange.emit(map);
    }

    private clearSourceLotSelectionFromOtherForms(sourceLotId: number, currentIndex: number): void {
        this.lotForms.forEach((_, index) => {
            const isCurrentForm = index === currentIndex;
            const hasSameSourceLotSelected =
                this.selectedSourceLotIdByFormIndex[index] === sourceLotId;

            if (isCurrentForm || !hasSameSourceLotSelected) {
                return;
            }

            delete this.selectedSourceLotIdByFormIndex[index];
            this.isSourceLotMappedByFormIndex[index] = false;
        });
    }

    private lotDtoToFormValue(lot: ILotMappedItem): any {
        return {
            // Address
            LotNumber: lot.lotNumber ?? null,
            DpNumber: lot.dpNumber ?? '',
            Street: lot.lotAddress?.street ?? '',
            Suburb: lot.lotAddress?.suburb ?? '',
            City: lot.lotAddress?.city ?? '',
            PostCode: lot.lotAddress?.postCode ?? '',
            AddressRegion: lot.lotAddress?.regionName ?? '',

            // Lot / Build
            LotContractType: LOT_CONTRACT_TYPE_ENUM[lot.contractType] ?? '',

            // Areas
            FloorArea: lot.amenities?.floorArea ?? null,
            LandArea: lot.amenities?.landArea ?? null,

            // Pricing
            PriceLand: lot.priceLand ?? null,
            PriceSell: lot.priceSell ?? null,
            ConGenFee: lot.contractGenerationFee ?? null,
            LotHoldDep: lot.lotHoldingDeposit ?? null,
            ExternalLandVendor: lot.thirdPartyLotVendor ?? '',
            LotPurchasePrice: lot.thirdPartyLotPurchasePrice ?? null,
            LotDeposit: lot.financials?.deposit ?? null,

            // Rooms
            NoBedrooms: lot.amenities?.noBedrooms ?? null,
            NoBathrooms: lot.amenities?.noBathrooms ?? null,
            NoLivingRooms: lot.amenities?.noLivingRooms ?? null,
            NoGarages: lot.amenities?.noGarages ?? null,
            NoCarparks: lot.amenities?.noCarparks ?? null,
            NoStudy: lot.amenities?.noStudy ?? null,

            // Dates
            EstJobStart: this.toDateOrNull(lot.jobStart),
            EstJobEnd: this.toDateOrNull(lot.jobComplete),

            // Flags
            IsKitset: !!lot.isKitset ? 1 : 0,
            IsKiwibuild: !!lot.isKiwibuild ? 1 : 0,
            IsTransportable: !!lot.isTransportable ? 1 : 0,
            IsGrowhome: !!lot.isGrowhome ? 1 : 0
        };
    }

    private getLotType(name: string): string {
        if (this.migrationLotType === LotTypeEnumId.TerracedBlock) {
            if (name.includes('Unit')) {
                return LOT_TYPE_ENUM[LotTypeEnumId.Unit];
            } else {
                return LOT_TYPE_ENUM[LotTypeEnumId.TerracedBlock];
            }
        }
        if (this.migrationLotType === LotTypeEnumId.Standalone) {
            return LOT_TYPE_ENUM[LotTypeEnumId.Standalone];
        }
        return '';
    }

    private isUnitName(name: string): boolean {
        return name.includes('Block') && name.includes('Unit');
    }

    private toDateOrNull(v: any): Date | null {
        if (!v) {
            return null;
        }
        if (v instanceof Date) {
            return v;
        }
        const s = typeof v === 'string' ? v : typeof v?.date === 'string' ? v.date : null;
        return s ? new Date(s) : null;
    }

    private uniqueLotNumbersValidator = (control: AbstractControl): ValidationErrors | null => {
        if (!(control instanceof FormArray)) {
            return null;
        }

        control.controls.forEach(group => {
            const lotNumberControl = group.get('LotNumber');
            this.setErrorFlag(
                lotNumberControl,
                LOT_MIGRATOR_FORM_ERRORS.duplicateLotNumber,
                false
            );
        });

        const seen = new Map<string, AbstractControl[]>();
        control.controls.forEach(group => {
            const lotNumberControl = group.get('LotNumber');
            const value = lotNumberControl?.value;
            if (value === null || value === undefined || value === '') {
                return;
            }
            const key = String(value);
            if (!seen.has(key)) {
                seen.set(key, []);
            }
            seen.get(key).push(lotNumberControl);

            if (this.unavailableLotNumbers.has(Number(key))) {
                this.setErrorFlag(
                    lotNumberControl,
                    LOT_MIGRATOR_FORM_ERRORS.duplicateLotNumber,
                    true
                );
            }
        });

        seen.forEach(controls => {
            if (controls.length <= 1) {
                return;
            }

            controls.forEach(c =>
                this.setErrorFlag(c, LOT_MIGRATOR_FORM_ERRORS.duplicateLotNumber, true)
            );
        });

        return null;
    };

    private setErrorFlag(
        control: AbstractControl | null,
        key: string,
        enabled: boolean
    ): void {
        if (!control) {
            return;
        }

        const errors = { ...(control.errors ?? {}) };
        const hasKey = !!errors[key];

        if (enabled && !hasKey) {
            errors[key] = true;
            control.setErrors(errors);
            return;
        }

        if (!enabled && hasKey) {
            delete errors[key];
            const hasOtherErrors = Object.keys(errors).length > 0;
            control.setErrors(hasOtherErrors ? errors : null);
        }
    }
}
